/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteQueuedMessagePathParameters,
  ListQueuedMessagesParams,
  ListQueuedMessagesPathParameters,
  RetryQueuedMessagePathParameters,
  SendQueuedMessageImmediatelyPathParameters,
  TLChatMessageResponse,
  TLHTTPValidationError,
  TLPaginatedResponseQueuedMessageItem,
  TLQueuedMessageItem,
  TLQueuedMessageResponse,
  TLUpdateQueuedMessageRequest,
  UpdateQueuedMessagePathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Queued Messages
 */
export const listQueuedMessages = (
  { conversationId }: ListQueuedMessagesPathParameters,
  params?: ListQueuedMessagesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseQueuedMessageItem>(
    { url: `/conversations/${conversationId}/queued-messages`, method: 'GET', params, signal },
    options,
  );
};

export const getListQueuedMessagesQueryKey = (
  { conversationId }: ListQueuedMessagesPathParameters,
  params?: ListQueuedMessagesParams,
) => {
  return ['conversations', conversationId, 'queued-messages', ...(params ? [params] : [])] as const;
};

export const getListQueuedMessagesQueryOptions = <
  TData = Awaited<ReturnType<typeof listQueuedMessages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: ListQueuedMessagesPathParameters,
  params?: ListQueuedMessagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listQueuedMessages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListQueuedMessagesQueryKey({ conversationId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listQueuedMessages>>> = ({ signal }) =>
    listQueuedMessages({ conversationId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listQueuedMessages>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListQueuedMessagesQueryResult = NonNullable<Awaited<ReturnType<typeof listQueuedMessages>>>;
export type ListQueuedMessagesQueryError = ErrorType<TLHTTPValidationError>;

export function useListQueuedMessages<
  TData = Awaited<ReturnType<typeof listQueuedMessages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListQueuedMessagesPathParameters,
  params: undefined | ListQueuedMessagesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listQueuedMessages>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listQueuedMessages>>,
          TError,
          Awaited<ReturnType<typeof listQueuedMessages>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListQueuedMessages<
  TData = Awaited<ReturnType<typeof listQueuedMessages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListQueuedMessagesPathParameters,
  params?: ListQueuedMessagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listQueuedMessages>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listQueuedMessages>>,
          TError,
          Awaited<ReturnType<typeof listQueuedMessages>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListQueuedMessages<
  TData = Awaited<ReturnType<typeof listQueuedMessages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListQueuedMessagesPathParameters,
  params?: ListQueuedMessagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listQueuedMessages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Queued Messages
 */

export function useListQueuedMessages<
  TData = Awaited<ReturnType<typeof listQueuedMessages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: ListQueuedMessagesPathParameters,
  params?: ListQueuedMessagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listQueuedMessages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListQueuedMessagesQueryOptions({ conversationId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Queued Messages
 */
export const prefetchListQueuedMessagesQuery = async <
  TData = Awaited<ReturnType<typeof listQueuedMessages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: ListQueuedMessagesPathParameters,
  params?: ListQueuedMessagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listQueuedMessages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListQueuedMessagesQueryOptions({ conversationId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Queued Messages
 */
export const invalidateListQueuedMessages = async (
  queryClient: QueryClient,
  { conversationId }: ListQueuedMessagesPathParameters,
  params?: ListQueuedMessagesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListQueuedMessagesQueryKey({ conversationId }, params) }, options);

  return queryClient;
};

/**
 * @summary Delete Queued Message
 */
export const deleteQueuedMessage = (
  { conversationId, queuedMessageId }: DeleteQueuedMessagePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    { url: `/conversations/${conversationId}/queued-messages/${queuedMessageId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteQueuedMessageQueryKey = ({
  conversationId,
  queuedMessageId,
}: DeleteQueuedMessagePathParameters) => {
  return ['DELETE', 'conversations', conversationId, 'queued-messages', queuedMessageId] as const;
};

export const getDeleteQueuedMessageQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, queuedMessageId }: DeleteQueuedMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteQueuedMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteQueuedMessageQueryKey({ conversationId, queuedMessageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteQueuedMessage>>> = ({ signal }) =>
    deleteQueuedMessage({ conversationId, queuedMessageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      conversationId !== null &&
      conversationId !== undefined &&
      queuedMessageId !== null &&
      queuedMessageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteQueuedMessage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteQueuedMessageQueryResult = NonNullable<Awaited<ReturnType<typeof deleteQueuedMessage>>>;
export type DeleteQueuedMessageQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteQueuedMessage<
  TData = Awaited<ReturnType<typeof deleteQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteQueuedMessagePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteQueuedMessage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteQueuedMessage>>,
          TError,
          Awaited<ReturnType<typeof deleteQueuedMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteQueuedMessage<
  TData = Awaited<ReturnType<typeof deleteQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteQueuedMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteQueuedMessage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteQueuedMessage>>,
          TError,
          Awaited<ReturnType<typeof deleteQueuedMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteQueuedMessage<
  TData = Awaited<ReturnType<typeof deleteQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteQueuedMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteQueuedMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Queued Message
 */

export function useDeleteQueuedMessage<
  TData = Awaited<ReturnType<typeof deleteQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, queuedMessageId }: DeleteQueuedMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteQueuedMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteQueuedMessageQueryOptions({ conversationId, queuedMessageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Queued Message
 */
export const prefetchDeleteQueuedMessageQuery = async <
  TData = Awaited<ReturnType<typeof deleteQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId, queuedMessageId }: DeleteQueuedMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteQueuedMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteQueuedMessageQueryOptions({ conversationId, queuedMessageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Queued Message
 */
export const invalidateDeleteQueuedMessage = async (
  queryClient: QueryClient,
  { conversationId, queuedMessageId }: DeleteQueuedMessagePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getDeleteQueuedMessageQueryKey({ conversationId, queuedMessageId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Queued Message
 */
export const updateQueuedMessage = (
  { conversationId, queuedMessageId }: UpdateQueuedMessagePathParameters,
  tLUpdateQueuedMessageRequest: BodyType<TLUpdateQueuedMessageRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLQueuedMessageItem>(
    {
      url: `/conversations/${conversationId}/queued-messages/${queuedMessageId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLUpdateQueuedMessageRequest,
      signal,
    },
    options,
  );
};

export const getUpdateQueuedMessageQueryKey = (
  { conversationId, queuedMessageId }: UpdateQueuedMessagePathParameters,
  tLUpdateQueuedMessageRequest?: BodyType<TLUpdateQueuedMessageRequest>,
) => {
  return [
    'PATCH',
    'conversations',
    conversationId,
    'queued-messages',
    queuedMessageId,
    tLUpdateQueuedMessageRequest,
  ] as const;
};

export const getUpdateQueuedMessageQueryOptions = <
  TData = Awaited<ReturnType<typeof updateQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, queuedMessageId }: UpdateQueuedMessagePathParameters,
  tLUpdateQueuedMessageRequest: BodyType<TLUpdateQueuedMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateQueuedMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getUpdateQueuedMessageQueryKey({ conversationId, queuedMessageId }, tLUpdateQueuedMessageRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateQueuedMessage>>> = ({ signal }) =>
    updateQueuedMessage({ conversationId, queuedMessageId }, tLUpdateQueuedMessageRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      conversationId !== null &&
      conversationId !== undefined &&
      queuedMessageId !== null &&
      queuedMessageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateQueuedMessage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateQueuedMessageQueryResult = NonNullable<Awaited<ReturnType<typeof updateQueuedMessage>>>;
export type UpdateQueuedMessageQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateQueuedMessage<
  TData = Awaited<ReturnType<typeof updateQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateQueuedMessagePathParameters,
  tLUpdateQueuedMessageRequest: BodyType<TLUpdateQueuedMessageRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateQueuedMessage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateQueuedMessage>>,
          TError,
          Awaited<ReturnType<typeof updateQueuedMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateQueuedMessage<
  TData = Awaited<ReturnType<typeof updateQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateQueuedMessagePathParameters,
  tLUpdateQueuedMessageRequest: BodyType<TLUpdateQueuedMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateQueuedMessage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateQueuedMessage>>,
          TError,
          Awaited<ReturnType<typeof updateQueuedMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateQueuedMessage<
  TData = Awaited<ReturnType<typeof updateQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateQueuedMessagePathParameters,
  tLUpdateQueuedMessageRequest: BodyType<TLUpdateQueuedMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateQueuedMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Queued Message
 */

export function useUpdateQueuedMessage<
  TData = Awaited<ReturnType<typeof updateQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, queuedMessageId }: UpdateQueuedMessagePathParameters,
  tLUpdateQueuedMessageRequest: BodyType<TLUpdateQueuedMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateQueuedMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateQueuedMessageQueryOptions(
    { conversationId, queuedMessageId },
    tLUpdateQueuedMessageRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Queued Message
 */
export const prefetchUpdateQueuedMessageQuery = async <
  TData = Awaited<ReturnType<typeof updateQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId, queuedMessageId }: UpdateQueuedMessagePathParameters,
  tLUpdateQueuedMessageRequest: BodyType<TLUpdateQueuedMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateQueuedMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateQueuedMessageQueryOptions(
    { conversationId, queuedMessageId },
    tLUpdateQueuedMessageRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Queued Message
 */
export const invalidateUpdateQueuedMessage = async (
  queryClient: QueryClient,
  { conversationId, queuedMessageId }: UpdateQueuedMessagePathParameters,
  tLUpdateQueuedMessageRequest: BodyType<TLUpdateQueuedMessageRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateQueuedMessageQueryKey({ conversationId, queuedMessageId }, tLUpdateQueuedMessageRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Retry Queued Message
 */
export const retryQueuedMessage = (
  { conversationId, queuedMessageId }: RetryQueuedMessagePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLQueuedMessageResponse>(
    { url: `/conversations/${conversationId}/queued-messages/${queuedMessageId}/retry`, method: 'POST', signal },
    options,
  );
};

export const getRetryQueuedMessageQueryKey = ({
  conversationId,
  queuedMessageId,
}: RetryQueuedMessagePathParameters) => {
  return ['POST', 'conversations', conversationId, 'queued-messages', queuedMessageId, 'retry'] as const;
};

export const getRetryQueuedMessageQueryOptions = <
  TData = Awaited<ReturnType<typeof retryQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, queuedMessageId }: RetryQueuedMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryQueuedMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRetryQueuedMessageQueryKey({ conversationId, queuedMessageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof retryQueuedMessage>>> = ({ signal }) =>
    retryQueuedMessage({ conversationId, queuedMessageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      conversationId !== null &&
      conversationId !== undefined &&
      queuedMessageId !== null &&
      queuedMessageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof retryQueuedMessage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RetryQueuedMessageQueryResult = NonNullable<Awaited<ReturnType<typeof retryQueuedMessage>>>;
export type RetryQueuedMessageQueryError = ErrorType<TLHTTPValidationError>;

export function useRetryQueuedMessage<
  TData = Awaited<ReturnType<typeof retryQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RetryQueuedMessagePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryQueuedMessage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof retryQueuedMessage>>,
          TError,
          Awaited<ReturnType<typeof retryQueuedMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRetryQueuedMessage<
  TData = Awaited<ReturnType<typeof retryQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RetryQueuedMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryQueuedMessage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof retryQueuedMessage>>,
          TError,
          Awaited<ReturnType<typeof retryQueuedMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRetryQueuedMessage<
  TData = Awaited<ReturnType<typeof retryQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RetryQueuedMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryQueuedMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retry Queued Message
 */

export function useRetryQueuedMessage<
  TData = Awaited<ReturnType<typeof retryQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, queuedMessageId }: RetryQueuedMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryQueuedMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRetryQueuedMessageQueryOptions({ conversationId, queuedMessageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Retry Queued Message
 */
export const prefetchRetryQueuedMessageQuery = async <
  TData = Awaited<ReturnType<typeof retryQueuedMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId, queuedMessageId }: RetryQueuedMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryQueuedMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRetryQueuedMessageQueryOptions({ conversationId, queuedMessageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Retry Queued Message
 */
export const invalidateRetryQueuedMessage = async (
  queryClient: QueryClient,
  { conversationId, queuedMessageId }: RetryQueuedMessagePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRetryQueuedMessageQueryKey({ conversationId, queuedMessageId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Send Queued Message Immediately
 */
export const sendQueuedMessageImmediately = (
  { conversationId, queuedMessageId }: SendQueuedMessageImmediatelyPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLChatMessageResponse>(
    {
      url: `/conversations/${conversationId}/queued-messages/${queuedMessageId}/send-immediately`,
      method: 'POST',
      signal,
    },
    options,
  );
};

export const getSendQueuedMessageImmediatelyQueryKey = ({
  conversationId,
  queuedMessageId,
}: SendQueuedMessageImmediatelyPathParameters) => {
  return ['POST', 'conversations', conversationId, 'queued-messages', queuedMessageId, 'send-immediately'] as const;
};

export const getSendQueuedMessageImmediatelyQueryOptions = <
  TData = Awaited<ReturnType<typeof sendQueuedMessageImmediately>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, queuedMessageId }: SendQueuedMessageImmediatelyPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendQueuedMessageImmediately>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getSendQueuedMessageImmediatelyQueryKey({ conversationId, queuedMessageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof sendQueuedMessageImmediately>>> = ({ signal }) =>
    sendQueuedMessageImmediately({ conversationId, queuedMessageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      conversationId !== null &&
      conversationId !== undefined &&
      queuedMessageId !== null &&
      queuedMessageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof sendQueuedMessageImmediately>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SendQueuedMessageImmediatelyQueryResult = NonNullable<
  Awaited<ReturnType<typeof sendQueuedMessageImmediately>>
>;
export type SendQueuedMessageImmediatelyQueryError = ErrorType<TLHTTPValidationError>;

export function useSendQueuedMessageImmediately<
  TData = Awaited<ReturnType<typeof sendQueuedMessageImmediately>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SendQueuedMessageImmediatelyPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendQueuedMessageImmediately>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof sendQueuedMessageImmediately>>,
          TError,
          Awaited<ReturnType<typeof sendQueuedMessageImmediately>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSendQueuedMessageImmediately<
  TData = Awaited<ReturnType<typeof sendQueuedMessageImmediately>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SendQueuedMessageImmediatelyPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendQueuedMessageImmediately>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof sendQueuedMessageImmediately>>,
          TError,
          Awaited<ReturnType<typeof sendQueuedMessageImmediately>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSendQueuedMessageImmediately<
  TData = Awaited<ReturnType<typeof sendQueuedMessageImmediately>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SendQueuedMessageImmediatelyPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendQueuedMessageImmediately>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Send Queued Message Immediately
 */

export function useSendQueuedMessageImmediately<
  TData = Awaited<ReturnType<typeof sendQueuedMessageImmediately>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, queuedMessageId }: SendQueuedMessageImmediatelyPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendQueuedMessageImmediately>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSendQueuedMessageImmediatelyQueryOptions({ conversationId, queuedMessageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Send Queued Message Immediately
 */
export const prefetchSendQueuedMessageImmediatelyQuery = async <
  TData = Awaited<ReturnType<typeof sendQueuedMessageImmediately>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId, queuedMessageId }: SendQueuedMessageImmediatelyPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendQueuedMessageImmediately>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSendQueuedMessageImmediatelyQueryOptions({ conversationId, queuedMessageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Send Queued Message Immediately
 */
export const invalidateSendQueuedMessageImmediately = async (
  queryClient: QueryClient,
  { conversationId, queuedMessageId }: SendQueuedMessageImmediatelyPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSendQueuedMessageImmediatelyQueryKey({ conversationId, queuedMessageId }) },
    options,
  );

  return queryClient;
};
